import { InlineLoading } from "../components/ContentLoading";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck, ChevronDown, CircleAlert, ShieldCheck, UserRound } from "lucide-react";
import { useState } from "react";
import {
  docVerificationState,
  OpsDocVerificationUpdateSchema,
  type OpsDocVerificationUpdate,
} from "../../shared/operations-docs-maintenance";
import type { OpsDocPage } from "../../shared/operations-docs-schemas";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Field, FieldLabel, FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import type { DocumentSession } from "../knowledge/document-session";
import { getDocOwners, updateDocOwner, updateDocVerification } from "./docs-maintenance-api";
import type { OperationsNavigate } from "./navigation";

type Props = {
  page: OpsDocPage;
  canEdit: boolean;
  session?: DocumentSession;
  navigate: OperationsNavigate;
};
function useDetailsMutation(props: Props) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      change: { ownerEmployeeId: string } | Omit<OpsDocVerificationUpdate, "expectedRevision">,
    ) => {
      let saved: OpsDocPage | null = props.page;
      const write = async (expectedRevision: number) => {
        const input = { ...change, expectedRevision };
        const response =
          "ownerEmployeeId" in input
            ? await updateDocOwner(props.page.id, input)
            : await updateDocVerification(props.page.id, input);
        saved = response.page;
        return saved?.revision ?? expectedRevision + 1;
      };
      if (props.session) await props.session.updateMetadata(write);
      else await write(props.page.revision);
      return saved;
    },
    onSuccess: async (page) => {
      if (page) queryClient.setQueryData(["ops", "docs", "page", page.id], { page });
      else {
        queryClient.removeQueries({ queryKey: ["ops", "docs", "page", props.page.id] });
        props.navigate("/docs");
      }
      await queryClient.invalidateQueries({ queryKey: ["ops", "docs", "bootstrap"] });
      await queryClient.invalidateQueries({ queryKey: ["ops", "docs", "reviews"] });
    },
  });
}

function useOwnerCandidates(pageId: string, open: boolean) {
  return useQuery({
    queryKey: ["ops", "docs", "owners", pageId],
    queryFn: () => getDocOwners(pageId),
    enabled: open,
    staleTime: 0,
  });
}
function OwnerDialog({
  page,
  action,
}: {
  page: OpsDocPage;
  action: ReturnType<typeof useDetailsMutation>;
}) {
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState(page.ownerEmployeeId);
  const [query, setQuery] = useState("");
  const candidates = useOwnerCandidates(page.id, open);
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) {
          setOwner(page.ownerEmployeeId);
          setQuery("");
          action.reset();
        }
      }}
    >
      <DialogTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 px-2 text-xs text-muted-foreground"
          />
        }
      >
        <UserRound className="size-3.5" />
        {page.ownerDisplayName}
        <ChevronDown className="size-3" />
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Page owner</DialogTitle>
          <DialogDescription>
            Choose an employee with access to this space. The owner manages the page. Transferring a
            private page removes your owner access.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Input
            aria-label="Find an employee"
            placeholder="Find an employee…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
          />
          <OwnerChoices candidates={candidates} query={query} owner={owner} setOwner={setOwner} />
          {(candidates.error ?? action.error) && (
            <FieldError>{(candidates.error ?? action.error)?.message}</FieldError>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            disabled={action.isPending || candidates.isPending || owner === page.ownerEmployeeId}
            onClick={() => {
              action.mutate(
                { ownerEmployeeId: owner },
                {
                  onSuccess: () => {
                    setOpen(false);
                  },
                },
              );
            }}
          >
            {action.isPending ? "Saving…" : "Assign owner"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function OwnerChoices({
  candidates,
  query,
  owner,
  setOwner,
}: {
  candidates: ReturnType<typeof useQuery<Awaited<ReturnType<typeof getDocOwners>>>>;
  query: string;
  owner: string;
  setOwner: (value: string) => void;
}) {
  return (
    <div className="max-h-64 overflow-y-auto" role="radiogroup" aria-label="Page owner">
      {candidates.data?.employees
        .filter((employee) =>
          `${employee.displayName} ${employee.username}`
            .toLocaleLowerCase()
            .includes(query.toLocaleLowerCase()),
        )
        .map((employee) => (
          <label
            className="flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-2 hover:bg-muted"
            key={employee.id}
          >
            <input
              type="radio"
              name="page-owner"
              checked={owner === employee.id}
              onChange={() => {
                setOwner(employee.id);
              }}
            />
            <span className="min-w-0">
              <span className="block text-sm">{employee.displayName}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {employee.username}
              </span>
            </span>
          </label>
        ))}
      {candidates.isPending && <InlineLoading label="Loading employees…" />}
      {candidates.data?.employees.length === 0 && <p>No eligible employees.</p>}
    </div>
  );
}
function VerificationForm({
  page,
  action,
  close,
}: {
  page: OpsDocPage;
  action: ReturnType<typeof useDetailsMutation>;
  close: () => void;
}) {
  const [verifiedAt, setVerifiedAt] = useState(page.verifiedAt ?? "");
  const [nextVerificationAt, setNextVerificationAt] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return page.nextVerificationAt && page.nextVerificationAt > today
      ? page.nextVerificationAt
      : new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10);
  });
  const [error, setError] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const save = (verifyNow: boolean, form: HTMLFormElement) => {
    const values = new FormData(form);
    const verified = z.string().parse(values.get("verifiedAt"));
    const next = z.string().parse(values.get("nextVerificationAt"));
    const input = OpsDocVerificationUpdateSchema.safeParse({
      expectedRevision: page.revision,
      verifiedAt: verifyNow ? today : verified || null,
      nextVerificationAt: next || null,
      confirmCurrentVersion: verifyNow || (verified !== "" && verified !== page.verifiedAt),
    });
    if (!input.success) {
      setError(input.error.issues[0]?.message ?? "Check the dates.");
      return;
    }
    const update = {
      verifiedAt: input.data.verifiedAt,
      nextVerificationAt: input.data.nextVerificationAt,
      confirmCurrentVersion: input.data.confirmCurrentVersion,
    };
    action.mutate(update, { onSuccess: close });
  };
  return (
    <DialogForm
      aria-label="Document verification"
      onSubmit={(event) => {
        event.preventDefault();
        save(false, event.currentTarget);
      }}
    >
      <DialogBody>
        <VerificationDates
          verifiedAt={verifiedAt}
          nextVerificationAt={nextVerificationAt}
          today={today}
          setVerifiedAt={setVerifiedAt}
          setNextVerificationAt={setNextVerificationAt}
        />
        <p className="text-xs text-muted-foreground">
          Setting a verification date confirms the current content was reviewed. Later edits or a
          due date will mark it as needing review. New or overdue reviews start with a suggested
          next review in 90 days; you can change or clear it.
        </p>
        {(error ?? action.error) && <FieldError>{error ?? action.error?.message}</FieldError>}
      </DialogBody>
      <DialogFooter>
        <Button type="submit" variant="outline" disabled={action.isPending}>
          Save dates
        </Button>
        <Button
          type="button"
          disabled={action.isPending}
          onClick={(event) => {
            if (event.currentTarget.form) save(true, event.currentTarget.form);
          }}
        >
          <ShieldCheck />
          {action.isPending ? "Saving…" : "Reviewed today"}
        </Button>
      </DialogFooter>
    </DialogForm>
  );
}
function VerificationDates({
  verifiedAt,
  nextVerificationAt,
  today,
  setVerifiedAt,
  setNextVerificationAt,
}: {
  verifiedAt: string;
  nextVerificationAt: string;
  today: string;
  setVerifiedAt: (value: string) => void;
  setNextVerificationAt: (value: string) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field>
        <FieldLabel htmlFor="verified-at">Verification date</FieldLabel>
        <Input
          id="verified-at"
          name="verifiedAt"
          type="date"
          max={today}
          value={verifiedAt}
          onChange={(event) => {
            setVerifiedAt(event.target.value);
          }}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="next-verification-at">Next verification</FieldLabel>
        <Input
          id="next-verification-at"
          name="nextVerificationAt"
          type="date"
          min={verifiedAt || undefined}
          value={nextVerificationAt}
          onChange={(event) => {
            setNextVerificationAt(event.target.value);
          }}
        />
      </Field>
    </div>
  );
}
function VerificationDialog({
  page,
  action,
}: {
  page: OpsDocPage;
  action: ReturnType<typeof useDetailsMutation>;
}) {
  const [open, setOpen] = useState(false);
  const state = docVerificationState(page);
  const Icon = state === "Needs review" ? CircleAlert : CalendarCheck;
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (value) action.reset();
      }}
    >
      <DialogTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 gap-1.5 px-2 text-xs ${state === "Needs review" ? "text-warning" : "text-muted-foreground"}`}
          />
        }
      >
        <Icon className="size-3.5" />
        {state}
        <ChevronDown className="size-3" />
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Document verification</DialogTitle>
          <DialogDescription>
            {page.verificationStale
              ? "This document has changed since its last verification."
              : "Keep this document current with a review schedule."}
          </DialogDescription>
        </DialogHeader>
        {open && (
          <VerificationForm
            page={page}
            action={action}
            close={() => {
              setOpen(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
export function DocsPageDetails(props: Props) {
  const action = useDetailsMutation(props);
  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      {props.canEdit && props.page.accessLevel === "manage" ? (
        <OwnerDialog page={props.page} action={action} />
      ) : (
        <span>Owned by {props.page.ownerDisplayName}</span>
      )}
      {props.canEdit ? (
        <VerificationDialog page={props.page} action={action} />
      ) : (
        <span>{docVerificationState(props.page)}</span>
      )}
      {props.page.nextVerificationAt && (
        <span title={`Last verified: ${props.page.verifiedAt ?? "Never"}`}>
          Next review {props.page.nextVerificationAt}
        </span>
      )}
    </div>
  );
}
